import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Avatar,
  Chip,
  Tooltip,
  Snackbar,
  Alert,
  Popover,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import LogoutIcon from '@mui/icons-material/Logout';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import { useAuth } from '../context/AuthContext';
import { db, doc, onSnapshot, updateDoc, getDoc, collection, getDocs, writeBatch } from '../components/FirebaseConfig';
import { apiBaseUrl, isSyncBackendAvailable } from '../services/api';
import MonitorLogin from './MonitorLogin';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { getAppTitle } from '../utils/version';

// ── Reutilización DIRECTA de las Cards Oficiales del Modo Integrado ──
import ScreenControlsCard from '../components/ScreenControlsCard';
import SearchCard from '../components/SearchCard';
import SearchResult from '../components/SearchResult';
import PlayListCard from '../components/PlayListCard';
import EffectsCard from '../components/EffectsCard';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#090a14',
      paper: '#121422',
    },
    primary: {
      main: '#ff2d9b',
    },
  },
  typography: {
    fontFamily: '"Outfit", "Inter", "Segoe UI", sans-serif',
  },
});

export default function RemoteControl() {
  const { user, setUser, logout } = useAuth();
  const effectiveUserId = user?.uid || '';
  const { t } = useTranslation();

  useEffect(() => {
    document.title = getAppTitle('Garaoke Remote');
  }, []);

  // Estado de conexión con la pantalla Desktop
  const [pcStatus, setPcStatus] = useState('connecting'); // 'online' | 'connecting'

  // Ancla para el submenú de usuario bajo el avatar
  const [anchorUserMenu, setAnchorUserMenu] = useState(null);

  // Pista en reproducción actual (sincronizada vía SSE y optimística)
  const [videoId, setVideoId] = useState('');

  // Superposición activa en pantalla (QR, Votos, Mensaje)
  const [activeOverlay, setActiveOverlay] = useState(null);

  // Volumen de pantalla sincronizado con Desktop
  const [screenVolume, setScreenVolume] = useState(100);

  // Listas de reproducción y configuración
  const [playlist, setPlaylist] = useState([]);
  const [currentPlaylist, setCurrentPlaylist] = useState('user_playlist');
  const [myPlaylists, setMyPlaylists] = useState([]);
  const [userPresetPlaylists, setUserPresetPlaylists] = useState([]);
  const [originPresetPlaylists, setOriginPresetPlaylists] = useState([]);
  const [originPresetPlaylist, setOriginPresetPlaylist] = useState('');
  const [userPresetPlaylist, setUserPresetPlaylist] = useState('');
  const [currentPlaylistItem, setCurrentPlaylistItem] = useState(null);

  // Intros y transiciones
  const [currentIntroVideo, setCurrentIntroVideo] = useState(
    process.env.VITE_DEFAULT_INTRO_VIDEO_CODE || process.env.REACT_APP_DEFAULT_INTRO_VIDEO_CODE || 'H_bB0sAqLNg'
  );
  const [currentTransitionVideo, setCurrentTransitionVideo] = useState(
    process.env.VITE_DEFAULT_TRANSITION_VIDEO_CODE || process.env.REACT_APP_DEFAULT_TRANSITION_VIDEO_CODE || 'wpXZZ4f_RKY'
  );

  // Búsqueda de canciones (aislada localmente en el móvil para explorar sin alterar la TV)
  const [keySearch, setKeySearch] = useState('');
  const [videosSearchResponse, setVideosSearchResponse] = useState([]);
  const [isKaraokeOnly, setIsKaraokeOnly] = useState(true);
  const [selectedApiKey, setSelectedApiKey] = useState('');

  // Ajustes de usuario (api keys, intros, etc.)
  const [userSettings, setUserSettings] = useState({
    configs: {
      general: { showControlsPlayer: true, allowInteractivity: true, theme: 'dark' },
      screenSettings: { volume: 100, playbackRate: 1.0, pitch: 0 },
      apiKeys: Array(5).fill(''),
      intros: Array(5).fill(''),
      transitions: Array(5).fill(''),
    },
    playlist: [],
    presetPlaylists: [],
    myPlaylists: [],
  });



  // Notificaciones tostada
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  // Vibración háptica en smartphone (solo si el usuario interactuó con la pantalla)
  const triggerHaptic = (duration = 45) => {
    if (typeof window !== 'undefined' && 'navigator' in window && typeof window.navigator.vibrate === 'function') {
      try {
        if (window.navigator.userActivation && !window.navigator.userActivation.hasBeenActive) {
          return;
        }
        if (typeof document !== 'undefined' && document.hasFocus && document.hasFocus()) {
          window.navigator.vibrate(duration);
        }
      } catch (e) { }
    }
  };

  const showNotification = (message, severity = 'error') => {
    if (severity === 'error' || severity === 'danger') {
      setToast({ open: true, message, severity: 'error' });
    }
  };

  // ── 1. Envío de Comandos Remotos a la Pantalla Desktop ──
  const sendCommand = useCallback(async (action, payload = {}) => {
    if (!user?.uid) return;
    triggerHaptic(40);

    // ID canónico único para deduplicación estricta entre SSE y Firestore
    const commandId = 'cmd_' + Date.now().toString(36) + '_' + Math.random().toString(36).substring(2, 8);
    const enrichedPayload = {
      ...(payload || {}),
      commandId,
      sender: 'remote',
    };

    // 1. Sincronización nativa en tiempo real por Firestore (funciona 100% en Netlify)
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        lastRemoteCommand: {
          action,
          payload: enrichedPayload,
          commandId,
          timestamp: Date.now(),
        }
      });
      setPcStatus('online');
    } catch (fsErr) {
      // Ignorar si Firestore está offline temporalmente
    }

    // 2. Si existe backend Express SSE (local o Render), enviar también por REST
    if (isSyncBackendAvailable) {
      try {
        const response = await fetch(`${apiBaseUrl}/sync/command`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            hostId: user.uid,
            action,
            commandId,
            payload: enrichedPayload,
          }),
        });
        if (response.ok) {
          setPcStatus('online');
        }
      } catch (err) {
        // Backend no disponible
      }
    }
  }, [user?.uid]);



  // ── 2. Reproducir Pista Inmediatamente en la Desktop ──
  const handlePlayNow = useCallback((targetVidId, forceFloating = false, isPlaylist = false, type = null, explicitStart = 0, explicitEnd = 0, explicitItem = null) => {
    if (!targetVidId) return;
    triggerHaptic(50);
    // Marca inmediatamente como en reproducción visual en el móvil (#00ff88)
    setVideoId(targetVidId);

    // Buscar ítem en lista activa o resultados de búsqueda
    const foundInPlaylist = Array.isArray(playlist) ? playlist.find(p => p.videoId === targetVidId || p.id === targetVidId) : null;
    const foundInSearch = Array.isArray(videosSearchResponse) ? videosSearchResponse.find(v => (v.id?.videoId || v.id || v.videoId) === targetVidId) : null;

    const sourceItem = explicitItem || foundInPlaylist || {};
    const title = sourceItem.title || foundInSearch?.snippet?.title || foundInSearch?.title || '';
    const thumbnail = sourceItem.thumbnail || foundInSearch?.snippet?.thumbnails?.default?.url || '';
    const assignedTo = sourceItem.assignedTo || '';
    const startTime = explicitStart > 0 ? explicitStart : Math.max(0, Number(sourceItem.startTime ?? sourceItem.t ?? 0) || 0);
    const endTime = explicitEnd > 0 ? explicitEnd : Math.max(0, Number(sourceItem.endTime ?? 0) || 0);

    const songItem = {
      videoId: targetVidId,
      title,
      thumbnail,
      assignedTo,
      startTime,
      endTime,
      votes: sourceItem.votes || [],
    };

    // Actualizar Firestore para persistencia y sincronización bidireccional
    if (user?.uid) {
      try {
        const userRef = doc(db, 'users', user.uid);
        updateDoc(userRef, {
          currentPlaybackState: {
            videoId: targetVidId,
            status: 'PLAYING',
            baseTime: startTime,
            startTime,
            endTime,
            startedAt: Date.now(),
            assignedTo,
            title,
          }
        }).catch(() => { });
      } catch (e) { }
    }

    sendCommand('PLAY_SONG', {
      videoId: targetVidId,
      startTime,
      endTime,
      item: songItem,
      title,
      assignedTo,
    });

    showNotification(`Reproduciendo en PC: "${(title || targetVidId).substring(0, 32)}..."`, 'info');
  }, [playlist, videosSearchResponse, user?.uid, sendCommand]);

  // ── 3. Sincronización en Tiempo Real con Firestore y Canal SSE ──
  useEffect(() => {
    if (!user?.uid) return;

    // A. Escuchar cambios en Firestore de la cuenta del usuario
    const userDocRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();

        if (Array.isArray(data.playlist)) {
          setPlaylist(data.playlist);
        }
        if (Array.isArray(data.myPlaylists)) {
          setMyPlaylists(data.myPlaylists);
        }
        if (Array.isArray(data.presetPlaylists)) {
          setUserPresetPlaylists(data.presetPlaylists);
        }

        if (data.currentPlaylist) {
          setCurrentPlaylist(data.currentPlaylist);
        }

        if (data.configs) {
          setUserSettings(prev => ({
            ...prev,
            configs: { ...prev.configs, ...data.configs },
          }));
        }

        // Si la PC guardó el estado de reproducción en Firestore
        if (data.currentPlaybackState) {
          try {
            const parsedState = typeof data.currentPlaybackState === 'string'
              ? JSON.parse(data.currentPlaybackState)
              : data.currentPlaybackState;
            if (parsedState?.videoId && parsedState?.status !== 'STOPPED') {
              setVideoId(parsedState.videoId);
              setCurrentPlaylistItem({
                videoId: parsedState.videoId,
                startTime: parsedState.baseTime || 0,
                endTime: parsedState.endTime || 0,
                title: parsedState.title || '',
                assignedTo: parsedState.assignedTo || '',
                item: {
                  videoId: parsedState.videoId,
                  title: parsedState.title || '',
                  assignedTo: parsedState.assignedTo || '',
                  startTime: parsedState.baseTime || 0,
                  endTime: parsedState.endTime || 0,
                }
              });
            } else if (parsedState?.status === 'STOPPED' || !parsedState?.videoId) {
              setVideoId('');
              setCurrentPlaylistItem(null);
            }
          } catch (e) { }
        } else if (data.currentVideoData) {
          try {
            const parsedData = typeof data.currentVideoData === 'string'
              ? JSON.parse(data.currentVideoData)
              : data.currentVideoData;
            if (parsedData?.videoId) {
              setVideoId(parsedData.videoId);
              setCurrentPlaylistItem({
                videoId: parsedData.videoId,
                startTime: parsedData.time || 0,
                endTime: parsedData.endTime || 0,
                title: parsedData.title || '',
                assignedTo: parsedData.assignedTo || '',
                item: {
                  videoId: parsedData.videoId,
                  title: parsedData.title || '',
                  assignedTo: parsedData.assignedTo || '',
                  startTime: parsedData.time || 0,
                  endTime: parsedData.endTime || 0,
                }
              });
            } else {
              setVideoId('');
              setCurrentPlaylistItem(null);
            }
          } catch (e) { }
        }

        if (data.activeOverlay !== undefined) {
          setActiveOverlay(data.activeOverlay);
        }

        // Sincronizar selección de intro y transición desde Desktop
        if (data.currentIntroVideo) {
          setCurrentIntroVideo(data.currentIntroVideo);
        }
        if (data.currentTransitionVideo) {
          setCurrentTransitionVideo(data.currentTransitionVideo);
        }

        // Sincronizar volumen de pantalla y volumen de efectos desde Desktop
        if (typeof data.effectsVolume === 'number') {
          try { localStorage.setItem('garaoke_effects_volume', String(data.effectsVolume)); } catch (e) { }
          window.dispatchEvent(new CustomEvent('garaoke_set_effects_volume', {
            detail: { volume: data.effectsVolume }
          }));
        }
        if (typeof data.screenVolume === 'number') {
          setScreenVolume(data.screenVolume);
        } else if (data.configs?.screenSettings?.volume !== undefined) {
          setScreenVolume(data.configs.screenSettings.volume);
        }

        // Sincronizar estado de efectos de sonido en vivo desde Desktop
        if (data.activeEffect !== undefined) {
          if (data.activeEffect && data.activeEffect.id) {
            // El efecto está activo en la consola anfitriona: marcar el botón
            window.dispatchEvent(new CustomEvent('garaoke_play_effect', {
              detail: {
                id: data.activeEffect.id,
                path: data.activeEffect.path,
                duration: data.activeEffect.duration,
                _fromDesktop: true,
              }
            }));
          } else {
            // El efecto terminó de reproducirse o se le dio stop en el anfitrión: desmarcar
            window.dispatchEvent(new CustomEvent('garaoke_stop_effect', { detail: { _fromDesktop: true } }));
          }
        }
      }
    });

    // B. Suscribirse al canal SSE de la PC anfitriona para latencia ultra baja (< 20ms) si hay backend Express
    let eventSource = null;
    if (isSyncBackendAvailable) {
      try {
        eventSource = new EventSource(`${apiBaseUrl}/sync/events/${user.uid}`);

        eventSource.onopen = () => {
          setPcStatus('online');
        };

        eventSource.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === 'INITIAL_STATE' || msg.type === 'STATE_CHANGE') {
              setPcStatus('online');
              if (msg.state?.videoId && msg.state?.status !== 'STOPPED') {
                setVideoId(msg.state.videoId);
                setCurrentPlaylistItem({
                  videoId: msg.state.videoId,
                  startTime: msg.state.startTime || 0,
                  endTime: msg.state.endTime || 0,
                  title: msg.state.title || '',
                  assignedTo: msg.state.assignedTo || '',
                  item: {
                    videoId: msg.state.videoId,
                    title: msg.state.title || '',
                    assignedTo: msg.state.assignedTo || '',
                    startTime: msg.state.startTime || 0,
                    endTime: msg.state.endTime || 0,
                  }
                });
              } else if (msg.state?.status === 'STOPPED' || msg.state?.videoId === null) {
                setVideoId('');
                setCurrentPlaylistItem(null);
              }
              if (msg.state?.activeOverlay !== undefined) {
                setActiveOverlay(msg.state.activeOverlay);
              }
            }
            if (msg.type === 'COMMAND_RECEIVED' && msg.command) {
              const { action, payload } = msg.command;
              if (action === 'TOGGLE_OVERLAY_QR') {
                setActiveOverlay(prev => prev === 'qr' ? null : 'qr');
              } else if (action === 'TOGGLE_OVERLAY_VOTES') {
                setActiveOverlay(prev => prev === 'votes' ? null : 'votes');
              } else if (action === 'TOGGLE_OVERLAY_MESSAGE') {
                setActiveOverlay(prev => prev === 'message' ? null : 'message');
              }
              if (action === 'CHANGE_INTRO' && payload?.introId) {
                setCurrentIntroVideo(payload.introId);
              }
              if (action === 'CHANGE_TRANSITION' && payload?.transitionId) {
                setCurrentTransitionVideo(payload.transitionId);
              }
              if (action === 'SET_EFFECTS_VOLUME' && typeof payload?.volume === 'number') {
                window.dispatchEvent(new CustomEvent('garaoke_set_effects_volume', {
                  detail: { volume: payload.volume }
                }));
              }
              if (action === 'SET_SCREEN_VOLUME' && typeof payload?.volume === 'number') {
                setScreenVolume(payload.volume);
              }
              if (action === 'PLAY_EFFECT' && payload?.id && payload?.sender !== 'remote') {
                window.dispatchEvent(new CustomEvent('garaoke_play_effect', {
                  detail: { id: payload.id, path: payload.path, duration: payload.duration, _fromDesktop: true }
                }));
              }
              if (action === 'STOP_EFFECT' && payload?.sender !== 'remote') {
                window.dispatchEvent(new CustomEvent('garaoke_stop_effect', {
                  detail: { id: payload?.id, path: payload?.path, _fromDesktop: true }
                }));
              }
              if (action === 'SELECT_PLAYLIST' && payload?.playlistId) {
                setCurrentPlaylist(payload.playlistId);
              }
              if ((action === 'PLAY_SONG' || action === 'SONG_STARTED') && payload?.videoId) {
                setVideoId(payload.videoId);
                const songStart = Math.max(0, Number(payload.startTime ?? payload.item?.startTime ?? payload.item?.t ?? 0) || 0);
                const songEnd = Math.max(0, Number(payload.endTime ?? payload.item?.endTime ?? 0) || 0);
                setCurrentPlaylistItem({
                  videoId: payload.videoId,
                  startTime: songStart,
                  endTime: songEnd,
                  title: payload.title || payload.item?.title || '',
                  assignedTo: payload.assignedTo || payload.item?.assignedTo || '',
                  item: payload.item || { videoId: payload.videoId, title: payload.title || '', assignedTo: payload.assignedTo || '' },
                });
              }
            }
          } catch (e) { }
        };

        eventSource.onerror = () => {
          // Si no hay backend SSE activo (ej. en Netlify estático), cerrar para evitar loop de conexión
          if (eventSource) {
            try { eventSource.close(); } catch (e) { }
          }
          // Firestore garantiza sincronización aún sin backend SSE
          setPcStatus('online');
        };
      } catch (e) { }
    }

    return () => {
      unsubscribe();
      if (eventSource) eventSource.close();
    };
  }, [user?.uid]);

  // ── Presencia del Control Remoto (Heartbeat para indicar a Desktop que está conectado) ──
  useEffect(() => {
    if (!user?.uid) return;

    const setPresence = (isActive) => {
      try {
        const userRef = doc(db, 'users', user.uid);
        updateDoc(userRef, {
          remoteActive: isActive,
          remoteLastSeen: Date.now()
        }).catch(() => { });
      } catch (e) { }
    };

    // Marcar activo inmediatamente
    setPresence(true);

    // Heartbeat cada 15 segundos
    const heartbeatInterval = setInterval(() => {
      setPresence(true);
    }, 15000);

    const handleBeforeUnload = () => {
      setPresence(false);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(heartbeatInterval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      setPresence(false);
    };
  }, [user?.uid]);

  // ── Selección y Cambio de Playlist (Replicado en Desktop vía SSE y Firestore) ──
  const handleSelectPlaylist = useCallback((newPlId) => {
    if (!newPlId) return;
    setCurrentPlaylist(newPlId);

    const found = myPlaylists.find(p => p.id === newPlId || p.name === newPlId)
      || userPresetPlaylists.find(p => p.name === newPlId)
      || originPresetPlaylists.find(p => p.name === newPlId);
    const plName = found?.name || newPlId;

    if (found?.items) {
      setPlaylist(found.items);
    }

    if (user?.uid) {
      try {
        const userRef = doc(db, 'users', user.uid);
        const updatePayload = { currentPlaylist: newPlId };
        if (found?.items) {
          updatePayload.playlist = found.items;
        }
        updateDoc(userRef, updatePayload).catch(() => { });
      } catch (e) { }
    }

    sendCommand('SELECT_PLAYLIST', {
      playlistId: newPlId,
      playlistName: plName,
    });
  }, [myPlaylists, userPresetPlaylists, originPresetPlaylists, user?.uid, sendCommand]);

  // ── Sincronización de Selección de Intros y Transiciones ──
  const handleChangeIntro = useCallback((newIntroId) => {
    if (!newIntroId) return;
    setCurrentIntroVideo(newIntroId);
    try { localStorage.setItem('garaoke_current_intro', newIntroId); } catch (e) { }
    if (user?.uid) {
      updateDoc(doc(db, 'users', user.uid), { currentIntroVideo: newIntroId }).catch(() => { });
    }
    sendCommand('CHANGE_INTRO', { introId: newIntroId });
  }, [user?.uid, sendCommand]);

  const handleChangeTransition = useCallback((newTransId) => {
    if (!newTransId) return;
    setCurrentTransitionVideo(newTransId);
    try { localStorage.setItem('garaoke_current_transition', newTransId); } catch (e) { }
    if (user?.uid) {
      updateDoc(doc(db, 'users', user.uid), { currentTransitionVideo: newTransId }).catch(() => { });
    }
    sendCommand('CHANGE_TRANSITION', { transitionId: newTransId });
  }, [user?.uid, sendCommand]);

  // ── Sincronización de Volumen de Pantalla ──
  const handleScreenVolumeChange = useCallback((val, commit) => {
    setScreenVolume(val);
    setUserSettings(prev => ({
      ...prev,
      configs: {
        ...prev.configs,
        screenSettings: { ...prev.configs?.screenSettings, volume: val }
      }
    }));
    if (commit && user?.uid) {
      try {
        const userRef = doc(db, 'users', user.uid);
        updateDoc(userRef, { 'configs.screenSettings.volume': val }).catch(() => { });
      } catch (e) { }
    }
    sendCommand('SET_SCREEN_VOLUME', { volume: val });
  }, [user?.uid, sendCommand]);

  // ── 4. Modificaciones de Playlist (Persisten en Firestore y Notifican a la PC) ──
  const handlePlaylistChange = useCallback(async (newPlaylist) => {
    setPlaylist(newPlaylist);
    if (!user?.uid) return;

    try {
      const userRef = doc(db, 'users', user.uid);
      const myPlaylistIdx = myPlaylists.findIndex(p => p.id === currentPlaylist || p.name === currentPlaylist);
      let updatedMyPlaylists = [...myPlaylists];

      if (myPlaylistIdx >= 0) {
        updatedMyPlaylists[myPlaylistIdx] = {
          ...updatedMyPlaylists[myPlaylistIdx],
          items: newPlaylist,
        };
        setMyPlaylists(updatedMyPlaylists);
        await updateDoc(userRef, { playlist: newPlaylist, myPlaylists: updatedMyPlaylists });
      } else {
        let updatedUserPresets = Array.isArray(userPresetPlaylists) ? [...userPresetPlaylists] : [];
        const presetIdx = updatedUserPresets.findIndex(p => p.name === currentPlaylist);
        if (presetIdx >= 0) {
          updatedUserPresets[presetIdx] = { ...updatedUserPresets[presetIdx], items: newPlaylist };
        } else {
          updatedUserPresets.push({ name: currentPlaylist, items: newPlaylist });
        }
        setUserPresetPlaylists(updatedUserPresets);
        await updateDoc(userRef, { playlist: newPlaylist, presetPlaylists: updatedUserPresets });
      }

      sendCommand('PLAYLIST_UPDATED');
    } catch (err) {
      console.warn('Error actualizando playlist:', err);
    }
  }, [user?.uid, myPlaylists, userPresetPlaylists, currentPlaylist, sendCommand]);

  // ── 5. Agregar Pista a la Playlist (Desde SearchResult) ──
  const handleAddToPlaylist = useCallback(async (video, targetPlaylistName = 'user_playlist', assignedTo = '') => {
    if (!user?.uid) return;
    triggerHaptic(50);

    const vidId = video.id?.videoId || video.id || video.videoId;
    const title = video.snippet?.title || video.title || 'Canción';
    const thumbnail = video.snippet?.thumbnails?.default?.url || video.thumbnail || '';

    const newItem = {
      id: Date.now().toString(),
      videoId: vidId,
      title,
      thumbnail,
      played: false,
      assignedTo: assignedTo || '',
      votes: [],
    };

    let updatedPlaylist = [...playlist];
    let updatedMyPlaylists = [...myPlaylists];

    const targetIdx = updatedMyPlaylists.findIndex(p => p.id === targetPlaylistName || p.name === targetPlaylistName);
    if (targetIdx >= 0) {
      const targetItems = Array.isArray(updatedMyPlaylists[targetIdx].items) ? [...updatedMyPlaylists[targetIdx].items] : [];
      targetItems.push(newItem);
      updatedMyPlaylists[targetIdx] = {
        ...updatedMyPlaylists[targetIdx],
        items: targetItems,
      };
      if (currentPlaylist === targetPlaylistName || currentPlaylist === updatedMyPlaylists[targetIdx].id) {
        updatedPlaylist = targetItems;
      }
    } else {
      updatedPlaylist.push(newItem);
    }

    setPlaylist(updatedPlaylist);
    setMyPlaylists(updatedMyPlaylists);

    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        playlist: updatedPlaylist,
        myPlaylists: updatedMyPlaylists,
      });
      sendCommand('PLAYLIST_UPDATED', { videoId: vidId, title });
    } catch (err) {
      console.warn('Error guardando pista en Firestore:', err);
      showNotification('No se pudo guardar la pista en la playlist', 'error');
    }
  }, [user?.uid, playlist, myPlaylists, currentPlaylist, sendCommand]);

  // ── 6. Crear, Renombrar y Eliminar Playlists ──
  const handleCreatePlaylist = useCallback(async (name) => {
    const newPl = { id: Date.now().toString(), name, items: [] };
    const updated = [...myPlaylists, newPl];
    setMyPlaylists(updated);
    setCurrentPlaylist(newPl.id);
    setPlaylist([]);
    if (user?.uid) {
      try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, { myPlaylists: updated, playlist: [], currentPlaylist: newPl.id });
        sendCommand('PLAYLIST_UPDATED');
        sendCommand('SELECT_PLAYLIST', { playlistId: newPl.id, playlistName: name });
      } catch (e) { }
    }
  }, [myPlaylists, user?.uid, sendCommand]);

  const handleRenamePlaylist = useCallback(async (idOrName, newName) => {
    const updated = myPlaylists.map(p => (p.id === idOrName || p.name === idOrName) ? { ...p, name: newName } : p);
    setMyPlaylists(updated);
    if (user?.uid) {
      try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, { myPlaylists: updated });
      } catch (e) { }
    }
  }, [myPlaylists, user?.uid]);

  const handleDeletePlaylist = useCallback(async (idOrName) => {
    let updated = myPlaylists.filter(p => p.id !== idOrName && p.name !== idOrName);
    if (updated.length === 0) {
      updated = [{ id: 'user_playlist', name: 'Mi lista', items: [] }];
    }
    setMyPlaylists(updated);
    const nextActive = updated[0];
    const nextId = nextActive.id || nextActive.name;
    setCurrentPlaylist(nextId);
    setPlaylist(nextActive.items || []);
    if (user?.uid) {
      try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, { myPlaylists: updated, playlist: nextActive.items || [], currentPlaylist: nextId });
        sendCommand('PLAYLIST_UPDATED');
        sendCommand('SELECT_PLAYLIST', { playlistId: nextId, playlistName: nextActive.name });
      } catch (e) { }
    }
  }, [myPlaylists, user?.uid, sendCommand]);

  const showModalQR = activeOverlay === 'qr';
  const showModalVotesTable = activeOverlay === 'votes';
  const showModalMessage = activeOverlay === 'message';

  const [votesCount, setVotesCount] = useState(0);

  // Escuchar cantidad de votos en tiempo real en subcolección 'votes'
  useEffect(() => {
    if (!effectiveUserId) {
      setVotesCount(0);
      return;
    }
    let unsubscribe = () => {};
    try {
      const votesColRef = collection(db, 'users', effectiveUserId, 'votes');
      unsubscribe = onSnapshot(votesColRef, (snapshot) => {
        setVotesCount(snapshot.size);
      }, (err) => {
        console.warn('Error escuchando subcolección votes en RemoteControl:', err);
      });
    } catch (e) {}

    return () => unsubscribe();
  }, [effectiveUserId]);

  const hasVotes = votesCount > 0 || (Array.isArray(playlist) && playlist.some(it => Array.isArray(it.votes) && it.votes.length > 0));

  const handleResetVotes = useCallback(async () => {
    if (!effectiveUserId) return;
    try {
      // 1. Eliminar documentos en subcolección 'votes'
      const votesColRef = collection(db, 'users', effectiveUserId, 'votes');
      const snap = await getDocs(votesColRef);
      if (!snap.empty) {
        const batch = writeBatch(db);
        snap.forEach(d => batch.delete(d.ref));
        await batch.commit();
      }

      // 2. Limpiar votes en playlist del usuario
      if (Array.isArray(playlist) && playlist.some(it => it.votes?.length > 0)) {
        const cleaned = playlist.map(it => ({ ...it, votes: [] }));
        setPlaylist(cleaned);
        const userRef = doc(db, 'users', effectiveUserId);
        await updateDoc(userRef, { playlist: cleaned }).catch(() => {});
      }

      // 3. Notificar vía BroadcastChannel
      try {
        if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
          const bc = new BroadcastChannel('garaoke_player_sync');
          bc.postMessage({ type: 'VOTES_RESET', timestamp: Date.now() });
          bc.close();
        }
      } catch (e) {}

      sendCommand('RESET_VOTES');
      showNotification('Todas las votaciones han sido reiniciadas', 'success');
    } catch (err) {
      console.error('Error reiniciando votos desde control remoto:', err);
      showNotification('Error al reiniciar las votaciones', 'error');
    }
  }, [effectiveUserId, playlist, sendCommand]);

  // Si el usuario no ha iniciado sesión, mostrar login para anfitrión
  if (!user) {
    return <MonitorLogin setUser={setUser} mode="remote" />;
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          background: 'radial-gradient(circle at 50% 5%, rgba(180, 77, 255, 0.12) 0%, rgba(9, 10, 20, 0.98) 75%), #090a14',
          color: '#ffffff',
          paddingBottom: '40px',
        }}
      >
        {/* Notificaciones flotantes (solo errores y excepciones con estilo danger) */}
        <Snackbar
          open={Boolean(toast.open && toast.message && (toast.severity === 'error' || toast.severity === 'danger'))}
          autoHideDuration={5000}
          onClose={() => setToast(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            severity="error"
            variant="filled"
            onClose={() => setToast(prev => ({ ...prev, open: false }))}
            sx={{
              width: '100%',
              maxWidth: '92vw',
              background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.96) 0%, rgba(153, 27, 27, 0.98) 100%)',
              color: '#ffffff',
              borderRadius: '12px',
              border: '1px solid rgba(254, 202, 202, 0.35)',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5), 0 0 20px rgba(239, 68, 68, 0.45)',
              fontWeight: 600,
              fontSize: '0.9rem',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              '& .MuiAlert-icon': {
                color: '#fff',
                fontSize: '1.3rem',
                alignItems: 'center',
              },
              '& .MuiAlert-action': {
                color: '#fff',
                paddingTop: 0,
                alignItems: 'center',
              },
              '& .MuiIconButton-root': {
                color: 'rgba(255, 255, 255, 0.85)',
                '&:hover': {
                  color: '#ffffff',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                },
              },
            }}
          >
            {toast.message}
          </Alert>
        </Snackbar>

        {/* ── HEADER SUPERIOR DEL CONTROL REMOTO ── */}
        <Box
          sx={{
            width: '100%',
            maxWidth: '560px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.2,
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(10px)',
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            background: 'rgba(9, 10, 20, 0.9)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #ff2d9b 0%, #b44dff 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 12px rgba(255, 45, 155, 0.4)',
              }}
            >
              <SmartphoneIcon sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', lineHeight: 1.1, letterSpacing: '0.5px' }}>
                GARAOKE <span style={{ color: '#ff2d9b' }}>REMOTE</span>
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.2 }}>
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    backgroundColor: pcStatus === 'online' ? '#00ff88' : '#ffb703',
                    boxShadow: pcStatus === 'online' ? '0 0 8px #00ff88' : 'none',
                  }}
                />
                <Typography sx={{ fontSize: '0.68rem', color: pcStatus === 'online' ? '#00ff88' : '#ffb703', fontWeight: 600 }}>
                  {pcStatus === 'online' ? t('remote.connectedPC') || 'PC Sincronizada' : t('remote.connecting') || 'Conectando a Desktop...'}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Avatar interactivo que abre el submenú de usuario */}
            <Tooltip title={user?.displayName || 'Cuenta'}>
              <IconButton
                size="small"
                onClick={(e) => setAnchorUserMenu(e.currentTarget)}
                sx={{
                  p: '2px',
                  background: 'linear-gradient(135deg, rgba(255, 45, 155, 0.6) 0%, rgba(180, 77, 255, 0.6) 100%)',
                  boxShadow: '0 0 10px rgba(255, 45, 155, 0.35)',
                  '&:hover': { transform: 'scale(1.08)' },
                }}
              >
                <Avatar
                  src={user?.photoURL}
                  sx={{ width: 30, height: 30, border: '2px solid #0d0617' }}
                />
              </IconButton>
            </Tooltip>

            {/* Submenú desplegable bajo el avatar */}
            <Popover
              open={Boolean(anchorUserMenu)}
              anchorEl={anchorUserMenu}
              onClose={() => setAnchorUserMenu(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: '#130924 !important',
                    background: '#130924 !important',
                    border: '1px solid rgba(255, 255, 255, 0.16) !important',
                    borderRadius: '16px !important',
                    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.95), 0 0 20px rgba(180, 77, 255, 0.2) !important',
                    p: 2,
                    minWidth: 260,
                    mt: 1,
                  }
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Perfil del usuario */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, pb: 1.2, borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
                  <Avatar src={user?.photoURL} sx={{ width: 38, height: 38, border: '2px solid #ff2d9b' }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user?.displayName || 'Usuario Garaoke'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255, 255, 255, 0.55)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user?.email || ''}
                    </Typography>
                  </Box>
                </Box>

                {/* Switch de idiomas dentro del submenú */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.2, py: 0.8, borderRadius: '10px', background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                  <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.75)', fontWeight: 600 }}>
                    {t('header.language') || 'Idioma'}
                  </Typography>
                  <LanguageSwitcher id="remote-user-menu-language-switcher" />
                </Box>

                {/* Cerrar sesión */}
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<LogoutIcon fontSize="small" />}
                  onClick={() => {
                    setAnchorUserMenu(null);
                    if (typeof logout === 'function') logout();
                    else setUser(null);
                  }}
                  sx={{
                    color: '#ff4d6d',
                    borderColor: 'rgba(255, 77, 109, 0.3)',
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#ff4d6d',
                      background: 'rgba(255, 77, 109, 0.1)',
                    }
                  }}
                >
                  {t('header.logout') || 'Cerrar sesión'}
                </Button>
              </Box>
            </Popover>
          </Box>
        </Box>

        {/* ── COLUMNA APILADA: RÉPLICA EXACTA DE LAS CARDS DE LA PANTALLA INTEGRADA ── */}
        <Box
          sx={{
            width: '100%',
            maxWidth: '560px',
            px: 1.5,
            py: 1.5,
            boxSizing: 'border-box',
          }}
        >
          <div className="pannel-column--stacked" style={{ height: 'auto', minHeight: 'calc(100vh - 80px)', overflowY: 'visible' }}>

            {/* 1. Controles de Pantalla (Intros, Transiciones, Mensaje, QR, Votos) */}
            <ScreenControlsCard
              currentIntroVideo={currentIntroVideo}
              currentTransitionVideo={currentTransitionVideo}
              handleChangeIntro={(e) => handleChangeIntro(e.target.value)}
              handleChangeTransition={(e) => handleChangeTransition(e.target.value)}
              openVideoPlayer={(vidId, autoplay, isPlaylist, type) => {
                if (type === 'intro') {
                  sendCommand('TRIGGER_INTRO', { videoId: vidId });
                  showNotification('Lanzando intro a la PC...', 'info');
                } else if (type === 'transition') {
                  sendCommand('TRIGGER_TRANSITION', { videoId: vidId });
                  showNotification('Lanzando transición a la PC...', 'info');
                } else {
                  handlePlayNow(vidId);
                }
              }}
              showHideModalMessage={(msg, e) => {
                e?.currentTarget?.blur();
                setActiveOverlay(prev => prev === 'message' ? null : 'message');
                sendCommand('TOGGLE_OVERLAY_MESSAGE', { message: msg });
                showNotification('Mensaje enviado a la pantalla', 'info');
              }}
              showModalMessage={showModalMessage}
              showHideModalQR={(e) => {
                e?.currentTarget?.blur();
                setActiveOverlay(prev => prev === 'qr' ? null : 'qr');
                sendCommand('TOGGLE_OVERLAY_QR');
                showNotification('Alternando QR en pantalla', 'info');
              }}
              showModalQR={showModalQR}
              showHideModalVotesTable={(e) => {
                e?.currentTarget?.blur();
                setActiveOverlay(prev => prev === 'votes' ? null : 'votes');
                sendCommand('TOGGLE_OVERLAY_VOTES');
                showNotification('Alternando votos en pantalla', 'info');
              }}
              showModalVotesTable={showModalVotesTable}
              hasVotes={hasVotes}
              onResetVotes={handleResetVotes}
              allowInteractivity={true}
              onToggleIntegratedScreen={() => { }}
              openConfigModal={() => { }}
              userSettings={userSettings}
              isRemoteControl={true}
              screenVolume={screenVolume}
              onUpdateScreenVolume={handleScreenVolumeChange}
              userId={effectiveUserId}
              onTriggerManualVotation={() => {
                sendCommand('TRIGGER_MANUAL_VOTATION');
              }}
              currentPlaylistItem={currentPlaylistItem}
              playlist={playlist}
              videoId={videoId}
            />

            {/* 2. Búsqueda de Canciones */}
            <div id="tour-search-bar" className="pannel-column-item-fixed">
              <SearchCard
                setKeySearch={setKeySearch}
                keySearch={keySearch}
                setVideosSearchResponse={setVideosSearchResponse}
                isKaraokeOnly={isKaraokeOnly}
                onIsKaraokeOnly={setIsKaraokeOnly}
                setSnackMessage={(msg) => {
                  const text = typeof msg === 'string' ? msg : msg?.text || '';
                  const type = typeof msg === 'object' ? (msg?.type || 'error') : 'error';
                  if (type === 'error' || type === 'danger') {
                    setToast({ open: true, message: text, severity: 'error' });
                  }
                }}
                userSettings={userSettings}
                selectedApiKey={selectedApiKey}
                setSelectedApiKey={setSelectedApiKey}
              />
            </div>

            {/* 3. Resultados de Búsqueda (Con altura fija y scroll interno idéntico a Desktop) */}
            <div style={{ height: 380, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
              <SearchResult
                videos={videosSearchResponse}
                setPreviewVideoId={() => { }}
                openVideoPlayer={handlePlayNow}
                setPlaylist={handlePlaylistChange}
                playlist={playlist}
                setReloadPlaylist={() => { }}
                currentPlaylist={currentPlaylist}
                myPlaylists={myPlaylists}
                userPresetPlaylists={userPresetPlaylists}
                onAddToPlaylist={handleAddToPlaylist}
                setCurrentPlaylistItem={setCurrentPlaylistItem}
                currentPlaylistItem={currentPlaylistItem}
                setVideoId={setVideoId}
                videoId={videoId}
                isIntegratedScreen={true}
                showPreview={false}
              />
            </div>

            {/* 4. Playlist de Reproducción (Reordenar, Asignar Cantante, PlayNow con brillo #00ff88) */}
            <div id="tour-playlist-card" style={{ height: 400, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
              <PlayListCard
                playlist={playlist}
                openVideoPlayer={handlePlayNow}
                videoPlayNow={handlePlayNow}
                setReloadPlaylist={() => { }}
                reloadPlaylist={false}
                setPlaylist={handlePlaylistChange}
                currentPlaylist={currentPlaylist}
                setCurrentPlaylist={handleSelectPlaylist}
                myPlaylists={myPlaylists}
                setMyPlaylists={setMyPlaylists}
                onCreatePlaylist={handleCreatePlaylist}
                onRenamePlaylist={handleRenamePlaylist}
                onDeletePlaylist={handleDeletePlaylist}
                userPresetPlaylists={userPresetPlaylists}
                originPresetPlaylists={originPresetPlaylists}
                setOriginPresetPlaylists={setOriginPresetPlaylists}
                originPresetPlaylist={originPresetPlaylist}
                setOriginPresetPlaylist={setOriginPresetPlaylist}
                userPresetPlaylist={userPresetPlaylist}
                setUserPresetPlaylist={setUserPresetPlaylist}
                setCurrentPlaylistItem={setCurrentPlaylistItem}
                currentPlaylistItem={currentPlaylistItem}
                setVideoId={setVideoId}
                videoId={videoId}
                isIntegratedScreen={true}
              />
            </div>

            {/* 5. Efectos de Sonido Oficiales (2.5x más alto para visualización táctil cómoda en mobile) */}
            <div id="tour-sound-effects" style={{ height: 650, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
              <EffectsCard
                onPlayRemote={(effect, volume) => {
                  triggerHaptic(45);
                  sendCommand('PLAY_EFFECT', { path: effect.path, volume, name: effect.name, id: effect.id, duration: effect.duration });
                  showNotification(`Efecto "${effect.name}" lanzado a los altavoces`, 'info');
                }}
                onStopRemote={(effect) => {
                  triggerHaptic(35);
                  sendCommand('STOP_EFFECT', { path: effect.path, name: effect.name, id: effect.id });
                  showNotification(`Efecto detenido`, 'info');
                }}
                onVolumeChange={(newVol) => {
                  if (user?.uid) {
                    updateDoc(doc(db, 'users', user.uid), { effectsVolume: newVol }).catch(() => { });
                  }
                  sendCommand('SET_EFFECTS_VOLUME', { volume: newVol });
                }}
              />
            </div>

          </div>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
